import { Chip, Box } from '@mui/material';
import {
  blueGrey,
  brown,
  cyan,
  green,
  grey,
  lime,
  orange,
  purple,
} from '@mui/material/colors';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { currencyFormatter } from 'core/utils/commonUtils';
import { currencyCol, renderCellExpand } from 'core/utils/muiGridUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import useOrders from 'hooks/swr/useOrders';
import useResponsive from 'hooks/useResponsive';
import CardedPage from 'layout/pages/CardedPage';
import { isNil } from 'lodash';
import { OrderActionEnum } from 'models/enums/orderActionEnum';
import { OrderStateEnum } from 'models/enums/orderStateEnum';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MobileOrdersList from './MobileOrderList';
import OrdersPageHeader from './OrdersPageHeader';
import OrdersTableToolbar from './OrdersTableToolbar';
import type { OrdersTableToolbarFilter } from './OrdersTableToolbar';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';
import type { Order } from 'models/order/order';
import type { FC } from 'react';

const OrdersPage: FC<OrdersPageProps> = ({ ordersType }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState({
    page: +(searchParams.get('pageIndex') ?? 0),
    pageSize: +(searchParams.get('pageSize') ?? 25),
  });
  const [searchText, setSearchText] = useState(searchParams.get('searchText') ?? '');

  const initFiltersFromUrl = (): OrdersTableToolbarFilter => {
    const actionsParam = searchParams.get('actions');
    const fromCreateDateParam = searchParams.get('fromCreateDate');
    const toCreateDateParam = searchParams.get('toCreateDate');
    const fromChangeStatusToOrderDateParam = searchParams.get(
      'fromChangeStatusToOrderDate'
    );
    const toChangeStatusToOrderDateParam = searchParams.get('toChangeStatusToOrderDate');

    return {
      actions: actionsParam ? JSON.parse(actionsParam) : [],
      fromCreateDate: fromCreateDateParam ? new Date(fromCreateDateParam) : null,
      toCreateDate: toCreateDateParam ? new Date(toCreateDateParam) : null,
      fromChangeStatusToOrderDate: fromChangeStatusToOrderDateParam
        ? new Date(fromChangeStatusToOrderDateParam)
        : null,
      toChangeStatusToOrderDate: toChangeStatusToOrderDateParam
        ? new Date(toChangeStatusToOrderDateParam)
        : null,
    };
  };

  const [filters, setFilters] = useState<OrdersTableToolbarFilter>(() => {
    const hasFiltersInUrl =
      searchParams.has('actions') ||
      searchParams.has('fromCreateDate') ||
      searchParams.has('toCreateDate') ||
      searchParams.has('fromChangeStatusToOrderDate') ||
      searchParams.has('toChangeStatusToOrderDate');

    if (hasFiltersInUrl) {
      return initFiltersFromUrl();
    }

    return {
      actions: [],
      fromCreateDate: null,
      toCreateDate: null,
      fromChangeStatusToOrderDate: null,
      toChangeStatusToOrderDate: null,
    };
  });

  const prevOrdersTypeRef = useRef<OrderStateEnum | null>(null);

  const navigate = useNavigate();
  const isSmallScreen = useResponsive('down', 'sm');

  const title = useMemo(
    () => ['Заявки', 'Заказы', 'Архив'][ordersType] ?? '',
    [ordersType]
  );

  // Сбрасываем фильтры только при изменении типа заказов (переходе между страницами)
  useEffect(() => {
    // Проверяем, изменился ли тип заказов
    if (prevOrdersTypeRef.current !== null && prevOrdersTypeRef.current !== ordersType) {
      setFilters({
        actions: [],
        fromCreateDate: null,
        toCreateDate: null,
        fromChangeStatusToOrderDate: null,
        toChangeStatusToOrderDate: null,
      });

      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }

    prevOrdersTypeRef.current = ordersType;
  }, [ordersType]);

  const columnVisibilityModel = {
    address: !isSmallScreen,
    sumPayments: !isSmallScreen,
    remark: !isSmallScreen,
  };

  const rowClickHandle = (params: GridRowParams): void => {
    navigate(String(params.row.id));
  };

  const mobileOrderClickHandle = (orderId: string): void => {
    navigate(orderId);
  };

  const mobilePaginationChangeHandle = (page: number): void => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const { data, isLoading } = useOrders({
    pageIndex: paginationModel.page,
    pageSize: paginationModel.pageSize,
    filterText: searchText,
    actions: filters.actions ?? [],
    states: [ordersType],
    createFrom: filters.fromCreateDate ?? undefined,
    createTo: filters.toCreateDate ?? undefined,
    dateChangeStateToOrderFrom: filters.fromChangeStatusToOrderDate ?? undefined,
    dateChangeStateToOrderTo: filters.toChangeStatusToOrderDate ?? undefined,
  });

  useEffect(() => {
    const params: Record<string, string> = {
      pageIndex: String(paginationModel.page),
      pageSize: String(paginationModel.pageSize),
      searchText,
    };

    if (filters.actions && filters.actions.length > 0) {
      params.actions = JSON.stringify(filters.actions);
    }

    if (!isNil(filters.fromCreateDate)) {
      params.fromCreateDate = filters.fromCreateDate.toISOString();
    }

    if (!isNil(filters.toCreateDate)) {
      params.toCreateDate = filters.toCreateDate.toISOString();
    }

    if (!isNil(filters.fromChangeStatusToOrderDate)) {
      params.fromChangeStatusToOrderDate =
        filters.fromChangeStatusToOrderDate.toISOString();
    }

    if (!isNil(filters.toChangeStatusToOrderDate)) {
      params.toChangeStatusToOrderDate = filters.toChangeStatusToOrderDate.toISOString();
    }

    setSearchParams(params);
  }, [setSearchParams, paginationModel, searchText, filters]);

  const onCreateClickHandle: VoidFunction = () => {
    navigate('new');
  };

  const getActionInfo = ({ action }: Order): { name: string; color: string } | null => {
    switch (action) {
      case OrderActionEnum.Request:
        return {
          name: getOrderActionName(action),
          color: orange[900],
        };
      case OrderActionEnum.Order:
        return {
          name: getOrderActionName(action),
          color: purple[500],
        };
      case OrderActionEnum.Measurement:
        return {
          name: getOrderActionName(action),
          color: brown[500],
        };
      case OrderActionEnum.Monitoring:
        return {
          name: getOrderActionName(action),
          color: blueGrey[500],
        };
      case OrderActionEnum.Monolith:
        return {
          name: getOrderActionName(action),
          color: lime[800],
        };
      case OrderActionEnum.Installation:
        return { name: getOrderActionName(action), color: cyan[600] };
      case OrderActionEnum.Completion:
        return {
          name: getOrderActionName(action),
          color: green[600],
        };
      default:
        return { name: 'Не задан', color: grey[500] };
    }
  };

  const columns: Array<GridColDef<Order>> = [
    {
      field: 'number',
      headerName: 'Номер',
      sortable: false,
    },
    {
      field: 'state',
      headerName: 'Этап',
      sortable: false,
      minWidth: 120,
      renderCell: (params) => {
        const statusInfo = getActionInfo(params.row);

        return (
          <Chip
            label={statusInfo?.name}
            sx={{
              borderColor: statusInfo?.color,
              color: statusInfo?.color,
            }}
            size='small'
            variant='outlined'
          />
        );
      },
    },
    {
      field: 'creationDate',
      headerName: 'Дата создания',
      sortable: false,
      type: 'date',
      minWidth: 150,
      valueGetter: (_, row) => {
        if (ordersType === OrderStateEnum.Request) {
          return row.dateCreate;
        } else if (ordersType === OrderStateEnum.Order) {
          return row.dateChangeStateToOrder;
        }

        if (row.dateChangeStateToOrder) {
          return row.dateChangeStateToOrder;
        }

        return row.dateCreate;
      },
    },
    {
      field: 'client',
      headerName: 'Клиент',
      sortable: false,
      flex: 1,
      maxWidth: 500,
      valueGetter: (_, row) => row.client.fullName,
    },
    {
      field: 'address',
      headerName: 'Адрес',
      sortable: false,
      flex: 1,
    },
    {
      field: 'sumPayments',
      headerName: 'Сумма платежей',
      sortable: false,
      minWidth: 150,
      renderCell: (params) =>
        params.row.sumPayments >= params.row.sum ? (
          <Chip
            label={currencyFormatter.format(params.value)}
            color='success'
            size='small'
            variant='outlined'
          />
        ) : (
          <span>{currencyFormatter.format(params.value)}</span>
        ),

      ...currencyCol,
    },
    {
      field: 'totalSum',
      headerName: 'Сумма',
      sortable: false,
      ...currencyCol,
    },
    {
      field: 'remark',
      headerName: 'Примечание',
      sortable: false,
      flex: 1,
      renderCell: renderCellExpand,
    },
  ];

  const filterChangeHandler = (newFilters: OrdersTableToolbarFilter): void => {
    setFilters(newFilters);
  };

  const filtersVisibility = useMemo(() => {
    if (ordersType === OrderStateEnum.Request) {
      return {
        actions: true,
        fromCreateDate: true,
        toCreateDate: true,
        fromChangeStatusToOrderDate: false,
        toChangeStatusToOrderDate: false,
      };
    } else if (ordersType === OrderStateEnum.Order) {
      return {
        actions: true,
        fromCreateDate: false,
        toCreateDate: false,
        fromChangeStatusToOrderDate: true,
        toChangeStatusToOrderDate: true,
      };
    } else {
      return {
        actions: true,
        fromCreateDate: false,
        toCreateDate: false,
        fromChangeStatusToOrderDate: false,
        toChangeStatusToOrderDate: false,
      };
    }
  }, [ordersType]);

  const Toolbar: FC = () => (
    <OrdersTableToolbar
      onFilterChange={filterChangeHandler}
      initFilters={filters}
      filtersVisibility={filtersVisibility}
    />
  );

  return (
    <CardedPage
      title={title}
      disablePadding
      header={
        <OrdersPageHeader
          title={title}
          onCreateClick={onCreateClickHandle}
          onSearchChanged={setSearchText}
          searchTextInit={searchText}
          hideCreateButton={ordersType !== OrderStateEnum.Request}
        />
      }>
      {isSmallScreen ? (
        <Box>
          <Box sx={{ px: 2, py: 1 }}>
            <Toolbar />
          </Box>

          {/* Список карточек */}
          <MobileOrdersList
            orders={data?.items ?? []}
            isLoading={isLoading}
            onOrderClick={mobileOrderClickHandle}
            getActionInfo={getActionInfo}
            ordersType={ordersType}
            paginationModel={paginationModel}
            totalCount={data?.totalCount ?? 0}
            onPaginationChange={mobilePaginationChangeHandle}
          />
        </Box>
      ) : (
        <DataGrid
          sx={{
            [`& .${gridClasses.cell}`]: {
              py: 2,
            },
            [`& .${gridClasses.cell}:focus`]: {
              outline: 'none',
            },
            [`& .${gridClasses.row}`]: {
              cursor: 'pointer',
            },
          }}
          rows={data?.items ?? []}
          rowCount={data?.totalCount ?? 0}
          loading={isLoading}
          columnVisibilityModel={columnVisibilityModel}
          disableColumnMenu
          slots={{ toolbar: Toolbar }}
          getRowHeight={() => 'auto'}
          autoHeight
          disableRowSelectionOnClick
          onRowClick={rowClickHandle}
          paginationModel={paginationModel}
          paginationMode='server'
          onPaginationModelChange={setPaginationModel}
          columns={columns}
        />
      )}
    </CardedPage>
  );
};

export interface OrdersPageProps {
  ordersType: OrderStateEnum;
}

export default OrdersPage;
